import { Box, useMediaQuery, useTheme } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import IFundTypeHistoryPeriod from '../../../../interfaces/expertise/history/IFundTypeHistoryPeriod';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import _ from 'lodash';

import am5locales_en_US from '@amcharts/amcharts5/locales/en_US';
import am5locales_fr_FR from '@amcharts/amcharts5/locales/fr_FR';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import { useTranslation } from 'react-i18next';
import { useAppSettings } from '../../../../../../app/features/settings/providers/AppSettingsProvider';
import investmentTypeHistoryLang from '../../langs/investmentTypeHistoryLang';
import AppLoaderGraph from '../../../../../../app/features/loader/graph/AppLoaderGraph';
import langInvestWordLang from '../../../../langs/langInvestWordLang';
import useAppFormatAmount from '../../../../../../app/features/format/hooks/useAppFormatAmount';
import CAppPaper from '../../../../../../app/components/CAppPaper';
import investmentFundLang from '../../../../langs/investmentFundLang';
import useAppFormatNumber from '../../../../../../app/features/format/hooks/useAppFormatNumber';
import { TInvestFundCurrency } from '../../../../types/fund/TInvestFundCurrency';
import CGraphMultiCurrency from '../../../../components/CGraphMultiCurrency';

type CHistoryJCurveProps = {
	historyPeriods: IFundTypeHistoryPeriod[] | null | undefined;
	btnPrevious: ReactNode;
	btnNext: ReactNode;
	allHistoryPeriods: IFundTypeHistoryPeriod[] | null | undefined;
};
const CHistoryJCurve = (props: CHistoryJCurveProps) => {
	const { historyPeriods, btnPrevious, btnNext, allHistoryPeriods } = props;

	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { rounding } = useAppSettings();
	const { displayRounding, displayRoundingSuffix, displayCurrencySymbol } = useAppFormatAmount();
	const { formatAmount } = useAppFormatNumber();

	const darkModeEnabled = theme.palette.mode === 'dark';

	const [data, setData] = useState<Record<string, unknown>[] | null>(null);

	const [currency, setCurrency] = useState<TInvestFundCurrency | null>(null);
	useEffect(() => {
		if (historyPeriods) {
			const currencies = _.uniq(historyPeriods.map((historyPeriod) => historyPeriod.stat.currency));
			setCurrency(currencies && currencies.length === 1 ? currencies[0] : null);
		}
	}, [historyPeriods]);

	useEffect(() => {
		if (historyPeriods) {
			setData(
				historyPeriods.map((x, index) => ({
					quarter: x.name,
					cumulatedDistributions: displayRounding(x.stat.cumulatedDistributions ?? 0, rounding),
					cumulatedDistributionsText: x.stat.cumulatedDistributions
						? formatAmount(displayRounding(x.stat.cumulatedDistributions ?? 0, rounding)) +
						  displayRoundingSuffix(rounding) +
						  displayCurrencySymbol(currency)
						: t(langInvestWordLang.nc.tag),
					totalCapitalCalls: displayRounding(x.stat.cumulatedCalledCapital ?? 0, rounding) * -1,
					totalCapitalCallsText: x.stat.cumulatedCalledCapital
						? formatAmount(displayRounding(x.stat.cumulatedCalledCapital ?? 0, rounding)) +
						  displayRoundingSuffix(rounding) +
						  displayCurrencySymbol(currency)
						: t(langInvestWordLang.nc.tag),
					callableCapital: displayRounding(x.stat.cumulatedCallableCapital ?? 0, rounding) * -1,
					callableCapitalText: x.stat.cumulatedCallableCapital
						? formatAmount(displayRounding(x.stat.cumulatedCallableCapital ?? 0, rounding)) +
						  displayRoundingSuffix(rounding) +
						  displayCurrencySymbol(currency)
						: t(langInvestWordLang.nc.tag),
					cumulatedCashflows: displayRounding(x.stat.cumulatedCashflows ?? 0, rounding),
					cumulatedCashflowsText: x.stat.cumulatedCashflows
						? formatAmount(displayRounding(x.stat.cumulatedCashflows ?? 0, rounding)) +
						  displayRoundingSuffix(rounding) +
						  displayCurrencySymbol(currency)
						: t(langInvestWordLang.nc.tag),
					strokeSettings: {
						strokeOpacity:
							historyPeriods[index + 1] && historyPeriods[index + 1].stat.cumulatedCashflows === null
								? 0
								: 1,
					},
					fillSettings: {
						fillOpacity:
							historyPeriods[index + 1] && historyPeriods[index + 1].stat.cumulatedCashflows === null
								? 0
								: 1,
					},
					columnSettings: {
						opacity:
							historyPeriods[index] && historyPeriods[index].stat.cumulatedCallableCapital === null
								? 0
								: 1,
					},
				})),
			);
		}
	}, [
		currency,
		displayCurrencySymbol,
		displayRounding,
		displayRoundingSuffix,
		formatAmount,
		historyPeriods,
		rounding,
		t,
	]);

	const [yMin, setYMin] = useState<number | null>(null);
	const [yMax, setYMax] = useState<number | null>(null);
	useEffect(() => {
		if (allHistoryPeriods) {
			setYMax(
				_.max(allHistoryPeriods.map((x) => displayRounding(x.stat.cumulatedDistributions ?? 0, rounding))) ??
					null,
			);
			setYMin(
				_.min(
					_.union(
						allHistoryPeriods
							.filter(
								(x) =>
									x.stat.cumulatedCalledCapital !== null &&
									x.stat.cumulatedCalledCapital !== 0 &&
									x.stat.cumulatedCallableCapital !== null &&
									x.stat.cumulatedCallableCapital !== 0,
							)
							.map((x) =>
								displayRounding(
									((x.stat.cumulatedCalledCapital ?? 0) + (x.stat.cumulatedCallableCapital ?? 0)) *
										-1,
									rounding,
								),
							),
						allHistoryPeriods
							.filter((x) => x.stat.cumulatedCashflows !== null)
							.map((x) => displayRounding(x.stat.cumulatedCashflows ?? 0, rounding)),
					),
				) ?? null,
			);
		}
	}, [allHistoryPeriods, displayRounding, rounding]);

	useEffect(() => {
		if (data && data.length > 0 && yMax !== null && yMin !== null && currency) {
			const root = am5.Root.new('cash-flows-graph');

			const chart = root.container.children.push(
				am5xy.XYChart.new(root, {
					layout: root.verticalLayout,
				}),
			);

			const lineXAxisColor = am5.Theme.new(root);
			lineXAxisColor.rule('Grid', ['base']).setAll({
				strokeOpacity: 1,
				stroke: am5.color('#1a2b48'),
				strokeWidth: 2,
			});

			if (darkModeEnabled) {
				root.setThemes([am5themes_Dark.new(root), am5themes_Responsive.new(root), lineXAxisColor]);
			} else {
				root.setThemes([am5themes_Responsive.new(root), lineXAxisColor]);
			}

			if (i18n.language === 'fr') {
				root.locale = am5locales_fr_FR;
			} else {
				root.locale = am5locales_en_US;
			}

			const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
			xRenderer.labels.template.setAll({
				centerY: am5.p50,
				paddingTop: 10,
				paddingBottom: 20,
				paddingRight: 15,
				textAlign: 'center',
				fontSize: '13px',
				fontFamily: "'Poppins', sans-serif",
				fontWeight: '500',
				fill: am5.color('#1A2B48'),
			});

			xRenderer.grid.template.setAll({
				visible: false,
			});

			const xAxis = chart.xAxes.push(
				am5xy.CategoryAxis.new(root, {
					categoryField: 'quarter',
					visible: true,
					opacity: 1,
					renderer: xRenderer,
				}),
			);

			xAxis.data.setAll(data);

			const yAxis = chart.yAxes.push(
				am5xy.ValueAxis.new(root, {
					min: yMin + (yMin * 5) / 100, // 5% de marge
					max: yMax + (yMax * 5) / 100, // 5% de marge
					// strokeWidth: 888,
					extraMax: 0.1,
					renderer: am5xy.AxisRendererY.new(root, {}),
				}),
			);

			const yRenderer = yAxis.get('renderer');
			yRenderer.labels.template.setAll({
				fill: am5.color(darkModeEnabled ? '#ccc' : '#AAA'),
				fontSize: '10px',
			});

			const tooltip = am5.Tooltip.new(root, {
				// Desactivate default background
				getFillFromSprite: false,
				// Desactivate default text color
				autoTextColor: false,
				pointerOrientation: 'left',
				labelText: `[bold]{quarter}[/]\n\n[width:200px]${t(
					investmentTypeHistoryLang.graph_legend_cumulated_distributions.tag,
				)}[/][#2ABDBB][bold]{cumulatedDistributionsText}[/][/][/]\n\n[width:200px]${t(
					investmentTypeHistoryLang.graph_legend_capital_calls.tag,
				)}[/][#86D9D8][bold]{totalCapitalCallsText}[/][/][/]\n\n[width:200px]${t(
					investmentTypeHistoryLang.graph_legend_dry_powder.tag,
				)}[/][${darkModeEnabled ? '#1A2B48' : '#FFF'}][bold]{callableCapitalText}[/][/][/]\n\n[width:200px]${t(
					investmentTypeHistoryLang.graph_legend_cumulated_cashflows.tag,
				)}[/][#B75AFF][bold]{cumulatedCashflowsText}[/][/][/]`,
			});

			tooltip?.get?.('background')?.setAll({
				fill: am5.color(darkModeEnabled ? '#fff' : '#1A2B48'),
				stroke: am5.color(darkModeEnabled ? '#fff' : '#1A2B48'),
				fillOpacity: darkModeEnabled ? 0.95 : 0.8,
			});

			const series1 = chart.series.push(
				am5xy.ColumnSeries.new(root, {
					name: t(investmentTypeHistoryLang.graph_legend_cumulated_distributions.tag),
					xAxis: xAxis,
					yAxis: yAxis,
					fill: am5.color(0x2abdbb),
					stroke: am5.color(0x2abdbb),
					valueYField: 'cumulatedDistributions',
					categoryXField: 'quarter',
				}),
			);

			series1.columns.template.setAll({
				tooltipY: am5.percent(10),
				templateField: 'columnSettings',

				cornerRadiusTL: 2,
				cornerRadiusTR: 2,
			});

			series1.data.setAll(data);

			const series2 = chart.series.push(
				am5xy.ColumnSeries.new(root, {
					name: t(investmentTypeHistoryLang.graph_legend_capital_calls.tag),
					xAxis: xAxis,
					yAxis: yAxis,
					tooltip: tooltip,
					stacked: true,
					fill: am5.color(0x86d9d8),
					stroke: am5.color(0x86d9d8),
					valueYField: 'totalCapitalCalls',
					categoryXField: 'quarter',
				}),
			);

			series2.columns.template.setAll({
				tooltipY: am5.percent(10),
				templateField: 'columnSettings',
			});

			series2.data.setAll(data);

			const series3 = chart.series.push(
				am5xy.ColumnSeries.new(root, {
					name: t(investmentTypeHistoryLang.graph_legend_dry_powder.tag),
					xAxis: xAxis,
					yAxis: yAxis,
					stacked: true,
					fill: am5.color(0x2c6172),
					stroke: am5.color(0x2c6172),
					valueYField: 'callableCapital',
					categoryXField: 'quarter',
				}),
			);

			series3.columns.template.setAll({
				tooltipY: am5.percent(10),
				templateField: 'columnSettings',
				cornerRadiusTL: 2,
				cornerRadiusTR: 2,
			});

			series3.data.setAll(data);

			const series4 = chart.series.push(
				am5xy.LineSeries.new(root, {
					name: t(investmentTypeHistoryLang.graph_legend_cumulated_cashflows.tag),
					xAxis: xAxis,
					yAxis: yAxis,
					fill: am5.color(0xb75aff),
					stroke: am5.color(0xb75aff),
					valueYField: 'cumulatedCashflows',
					categoryXField: 'quarter',
				}),
			);

			series4.strokes.template.setAll({
				strokeWidth: 3,
				templateField: 'strokeSettings',
			});

			series4.fills.template.setAll({
				templateField: 'fillSettings',
			});

			series4.data.setAll(data);

			series1.columns.template.setAll({
				width: am5.percent(30),
			});
			series2.columns.template.setAll({
				width: am5.percent(30),
				tooltipX: am5.percent(0),
			});
			series3.columns.template.setAll({
				width: am5.percent(30),
			});

			chart.set('cursor', am5xy.XYCursor.new(root, {}));

			const cursor = chart.get('cursor');
			cursor?.lineY.setAll({
				visible: false,
			});
			cursor?.lineX.setAll({
				visible: false,
			});

			let legend: am5.Legend;
			let legendRoot: am5.Root;
			if (isMobile) {
				legendRoot = am5.Root.new('cash-flows-graph-legend');
				legend = legendRoot.container.children.push(
					am5.Legend.new(legendRoot, {
						centerX: am5.percent(50),
						x: am5.percent(50),
						layout: legendRoot.verticalLayout,
						useDefaultMarker: true,
						height: am5.percent(100),
						verticalScrollbar: am5.Scrollbar.new(legendRoot, {
							orientation: 'vertical',
						}),
					}),
				);

				legend.markerRectangles.template.setAll({
					cornerRadiusTL: 10,
					cornerRadiusTR: 10,
					cornerRadiusBL: 10,
					cornerRadiusBR: 10,
				});
				legend.labels.template.setAll({
					fontSize: 14,
					fontWeight: '300',
					fill: am5.color(theme.palette.primary.main),
				});
				legend.valueLabels.template.setAll({
					fontSize: 14,
					fontWeight: '400',
					fill: am5.color(theme.palette.primary.main),
				});
			} else {
				legend = chart.children.push(
					am5.Legend.new(root, {
						centerX: am5.p50,
						centerY: am5.p50,
						x: am5.p50,
						y: am5.p100,
						// fontFamily: "'Poppins', sans-serif",
					}),
				);
			}

			legend.data.setAll(chart.series.values);

			chart.appear(1000, 100);
			series1.appear();

			return () => {
				root.dispose();
				isMobile ? legendRoot.dispose() : null;
			};
		}
	}, [
		historyPeriods,
		data,
		t,
		darkModeEnabled,
		rounding,
		i18n,
		yMin,
		yMax,
		isMobile,
		theme.palette.primary.main,
		currency,
	]);

	return (
		<>
			{data !== null ? (
				<>
					{data.length === 0 ? (
						<>
							<CAppPaper
								variant="outlined"
								sx={{
									textAlign: 'center',
									padding: '50px',
									width: '100%',
								}}
							>
								{t(investmentFundLang.text_no_data_available_yet.tag)}
							</CAppPaper>
						</>
					) : (
						<>
							{currency ? (
								<>
									<Box sx={{ position: 'relative', height: '280px' }}>
										<Box sx={{ position: 'absolute', top: 'calc(50% - 32px)', left: '0px' }}>
											{btnPrevious}
										</Box>
										<Box sx={{ position: 'absolute', top: 'calc(50% - 32px)', right: '0px' }}>
											{btnNext}
										</Box>
										<Box
											id="cash-flows-graph"
											sx={{ height: '300px', width: '100%', marginLeft: '-10px' }}
										/>
									</Box>
									<Box
										id="cash-flows-graph-legend"
										sx={{
											height: '130px',
											display: { xs: 'block', sm: 'none' },
											marginBottom: '20px',
										}}
									/>
								</>
							) : (
								<CGraphMultiCurrency height={280} variant="rectangular" />
							)}
						</>
					)}
				</>
			) : (
				<AppLoaderGraph height={280} />
			)}
		</>
	);
};

export default CHistoryJCurve;
